import { placeAddedNodes } from "./ai/system-edit-placement";
import { absolutePosition } from "./auto-fuse";
import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import type { Cable, ComponentDefinition, ComponentNode, SystemNode } from "./types";

const PADDING = 32;
const HEADER = 48;

/** Pure layout plan. Only selected components move; zone membership, rotations,
 * electrical settings and unselected positions stay intact. Containers grow
 * to the right/bottom only when the arranged members need more room. */
export function layoutSelectedComponents(
  nodes: SystemNode[],
  cables: Cable[],
  ids: string[],
): SystemNode[] {
  const selected = new Set(ids);
  const additions = nodes.filter((node): node is ComponentNode => node.kind === "component" && selected.has(node.id));
  if (!additions.length) return nodes;
  const copies = new Map(additions.map(node => [node.id, { ...node, position: { ...node.position } }]));
  const result = nodes.map(node => copies.get(node.id) ?? node);
  const byId = new Map(nodes.map(node => [node.id, node]));
  const definitions = new Map<string, ComponentDefinition>();
  for (const node of nodes) {
    if (node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (def) definitions.set(node.id, def);
  }
  const rect = (node: ComponentNode) => ({
    ...absolutePosition(node, nodes),
    ...(definitions.has(node.id) ? effectiveDimensions(definitions.get(node.id)!, node) : { width: 120, height: 80 }),
  });
  const selectedRects = additions.map(rect);
  const fallbackCenter = {
    x: (Math.min(...selectedRects.map(r => r.x)) + Math.max(...selectedRects.map(r => r.x + r.width))) / 2,
    y: (Math.min(...selectedRects.map(r => r.y)) + Math.max(...selectedRects.map(r => r.y + r.height))) / 2,
  };
  const boundsByNode = new Map<string, { x: number; y: number; width: number; height: number }>();
  for (const node of additions) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent?.kind !== "zone") continue;
    const origin = absolutePosition(parent, nodes);
    boundsByNode.set(node.id, {
      x: origin.x + PADDING, y: origin.y + HEADER,
      width: Math.max(0, parent.width - PADDING * 2),
      height: Math.max(0, parent.height - HEADER - PADDING),
    });
  }
  // All routes will be redrawn, so obsolete bends must not bias placement.
  placeAddedNodes([...copies.values()], result, definitions,
    cables.map(cable => ({ ...cable, waypoints: undefined })), { fallbackCenter, boundsByNode });

  // The placement engine returns absolute coordinates; persisted members use
  // parent-relative positions, including through nested zones.
  for (const node of copies.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (!parent) continue;
    const origin = absolutePosition(parent, nodes);
    node.position = { x: node.position.x - origin.x, y: node.position.y - origin.y };
  }
  const planned = new Map(result.map(node => [node.id, node]));
  for (const component of copies.values()) {
    let child: SystemNode = component;
    const seen = new Set([child.id]);
    while (child.parentId && !seen.has(child.parentId)) {
      seen.add(child.parentId);
      const parent = planned.get(child.parentId);
      if (parent?.kind !== "zone") break;
      const size = child.kind === "zone" ? child : rect(child);
      const width = Math.max(parent.width, child.position.x + size.width + PADDING);
      const height = Math.max(parent.height, child.position.y + size.height + PADDING);
      const grown = width === parent.width && height === parent.height ? parent : { ...parent, width, height };
      planned.set(parent.id, grown);
      child = grown;
    }
  }
  return result.map(node => planned.get(node.id)!);
}
